import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AccentProvider } from './context/AccentContext';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './pages/LoginPage';
import { AnalysesPage } from './pages/AnalysesPage';
import { AdminPage } from './pages/AdminPage';
import { ProfilPage } from './pages/ProfilPage';
import { NotificationsPage } from './pages/NotificationsPage';

export type Page = 'analyses' | 'analyses-esp' | 'analyses-mes' | 'analyses-det' | 'analyses-mod' | 'admin' | 'profil' | 'notifications';

const PAGES: Page[] = ['analyses', 'analyses-esp', 'analyses-mes', 'analyses-det', 'analyses-mod', 'admin', 'profil', 'notifications'];

function pageFromHash(): Page {
  const hash = window.location.hash.replace(/^#/, '').split('?')[0];
  // `prestations` (ancienne route) repointée vers la vue d'analyses.
  if (hash === 'prestations') return 'analyses';
  return (PAGES as string[]).includes(hash) ? (hash as Page) : 'analyses';
}

function AppContent() {
  const { user, loading, isAdmin } = useAuth();
  const [page, setPage] = useState<Page>(pageFromHash);

  useEffect(() => {
    const onHashChange = () => setPage(pageFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const effectivePage: Page = page === 'admin' && !isAdmin ? 'analyses' : page;

  const navigate = (p: Page) => {
    window.location.hash = p;
  };

  return (
    <AppShell currentPage={effectivePage} onNavigate={navigate}>
      {effectivePage === 'analyses' && <AnalysesPage section="part" />}
      {effectivePage === 'analyses-esp' && <AnalysesPage section="esp" />}
      {effectivePage === 'analyses-mes' && <AnalysesPage section="mes" />}
      {effectivePage === 'analyses-det' && <AnalysesPage section="det" />}
      {effectivePage === 'analyses-mod' && <AnalysesPage section="mod" />}
      {effectivePage === 'admin' && <AdminPage />}
      {effectivePage === 'profil' && <ProfilPage />}
      {effectivePage === 'notifications' && <NotificationsPage />}
    </AppShell>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AccentProvider>
          <AppContent />
        </AccentProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
